import { useEffect, useState } from "react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import CustomTable, { ColumnConfig } from "components/CustomTable";
import { Button } from "components/ui/button";
import { useAdminAuth } from "hooks/useAdminAuth";
import {
  AdminIntegrationStatus,
  IntegrationState,
  getIntegracoesStatus,
  setIntegracaoStatus,
  getIntegracoesLogs,
  testarAlertaSlack,
} from "services/admin/adminIntegracoes.service";

const STATE_LABEL: Record<IntegrationState, string> = {
  operational: "🟢 Operacional",
  maintenance: "🟡 Manutenção",
  instability: "🔴 Instabilidade",
};

const STATE_TONE: Record<IntegrationState, "green" | "yellow" | "red"> = {
  operational: "green",
  maintenance: "yellow",
  instability: "red",
};

type LogRow = { integracao: string; status: React.ReactNode; mensagem: string; data: string };
const logColumns: ColumnConfig<LogRow>[] = [
  { key: "integracao", header: "Integração" },
  { key: "status", header: "Status" },
  { key: "mensagem", header: "Mensagem" },
  { key: "data", header: "Data" },
];

const AdminIntegracoes = () => {
  const { admin } = useAdminAuth();
  const canEdit = admin && ["admin", "developer", "operations"].includes(admin.role);

  const [statuses, setStatuses] = useState<AdminIntegrationStatus[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [testandoAlerta, setTestandoAlerta] = useState(false);
  const [alertaFeedback, setAlertaFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([getIntegracoesStatus(), getIntegracoesLogs({ limit: 30 })])
      .then(([s, l]) => {
        setStatuses(s);
        setLogs(l.data);
      })
      .catch((err) => console.error("[AdminIntegracoes] erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleChangeState = async (key: string, state: IntegrationState) => {
    try {
      await setIntegracaoStatus(key, state);
      load();
    } catch (err) {
      console.error("[AdminIntegracoes] erro ao atualizar status:", err);
    }
  };

  const handleTestarAlerta = async () => {
    setTestandoAlerta(true);
    setAlertaFeedback(null);
    try {
      await testarAlertaSlack();
      setAlertaFeedback({ ok: true, msg: "Alerta de teste enviado! Confira o canal do Slack." });
    } catch (err: any) {
      setAlertaFeedback({
        ok: false,
        msg: err?.response?.data?.error || "Falha ao enviar alerta de teste.",
      });
    } finally {
      setTestandoAlerta(false);
    }
  };

  const logRows: LogRow[] = logs.map((l) => ({
    integracao: l.integration,
    status: <StatusBadge label={l.status} tone={l.status === "ok" ? "green" : "red"} />,
    mensagem: l.message || "—",
    data: new Date(l.createdAt).toLocaleString("pt-BR"),
  }));

  return (
    <AdminLayout title="Integrações" subtitle="Monitorado automaticamente a cada 10 minutos">
      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="-mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-400 max-w-2xl">
              Banco de dados, UPS e FedEx são checados com uma chamada real; Shopify e Nuvemshop são inferidos pela taxa
              de erro recente. Marcar como "Manutenção" pausa a checagem automática até você tirar manualmente desse
              estado. Quando o estado muda, um alerta é enviado automaticamente pro Slack (se configurado).
            </p>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={handleTestarAlerta} disabled={testandoAlerta}>
                {testandoAlerta ? "Enviando..." : "Testar alerta Slack"}
              </Button>
            )}
          </div>
          {alertaFeedback && (
            <p className={`-mt-4 text-xs ${alertaFeedback.ok ? "text-green-600" : "text-red-600"}`}>
              {alertaFeedback.msg}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {statuses.map((s) => (
              <AdminCard key={s.key} className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-800">{s.label}</h3>
                  <StatusBadge label={STATE_LABEL[s.state]} tone={STATE_TONE[s.state]} />
                </div>
                <div className="text-sm text-slate-500 flex flex-col gap-1">
                  <span>Tempo de resposta: {s.responseTimeMs != null ? `${s.responseTimeMs}ms` : "—"}</span>
                  <span>Última verificação: {s.lastSync ? new Date(s.lastSync).toLocaleString("pt-BR") : "—"}</span>
                  <span>Erros (24h): {s.errorCount24h}</span>
                  {s.message && <span className="text-slate-400 italic">{s.message}</span>}
                </div>
                {canEdit && (
                  <select
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs mt-auto"
                    value={s.state}
                    onChange={(e) => handleChangeState(s.key, e.target.value as IntegrationState)}
                  >
                    <option value="operational">Operacional</option>
                    <option value="maintenance">Manutenção</option>
                    <option value="instability">Instabilidade</option>
                  </select>
                )}
              </AdminCard>
            ))}
          </div>

          <AdminCard title="Últimos logs" action={<Button variant="outline" onClick={load}>Atualizar</Button>}>
            <CustomTable objectList={logRows} columns={logColumns} />
          </AdminCard>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminIntegracoes;
